"use client";

import React from "react";
import { 
  Box, 
  Heading, 
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Spinner,
  Flex,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Progress,
  Badge,
  Button,
  HStack
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import type { Meeting as ApiMeeting, Pagination } from "../../services/meetingService";

interface MeetingHistoryProps {
  salesExecutiveName?: string;
  meetings: ApiMeeting[];
  isLoading: boolean;
  error: string | null;
  pagination: Pagination | null;
  onPageChange: (page: number) => void;
}

const MeetingRow: React.FC<{
  client: string;
  date: string;
  duration: string;
  note: string;
  scorePercentage?: number;
  status?: string;
  onClick: () => void;
  hasDetails: boolean;
}> = ({ 
  client, 
  date, 
  duration, 
  note, 
  scorePercentage,
  status,
  onClick, 
  hasDetails 
}) => {
  return (
    <Tooltip
      label={hasDetails ? "Click to view meeting details" : "Detailed view not available for this meeting"}
      placement="top"
      hasArrow
    >
      <Tr 
        cursor={hasDetails ? "pointer" : "default"} 
        _hover={{ bg: hasDetails ? "gray.50" : "initial" }} 
        onClick={hasDetails ? onClick : undefined}
        transition="background-color 0.2s"
        opacity={hasDetails ? 1 : 0.7}
      >
        <Td width="150px" fontSize="xs" fontWeight="semibold" color="gray.500" py={3}>
          {client}
        </Td>
        <Td width="130px" fontSize="xs" fontWeight="semibold" color="gray.500" py={3}>
          {date}
        </Td>
        <Td fontSize="xs" fontWeight="semibold" color="gray.500" py={3}>
          {duration}
        </Td>
        <Td width="150px" py={3}>
          {scorePercentage !== undefined ? (
            <Box py={1.5}>
              <Progress 
                value={scorePercentage || 0} 
                max={100}
                size="xs"
                colorScheme={(scorePercentage || 0) > 50 ? "blue" : "orange"}
                borderRadius="full"
                bg="gray.200"
              />
            </Box>
          ) : (
            <Box h="1px" bg="gray.300" />
          )}
        </Td>
        <Td width="200px" fontSize="xs" fontWeight="semibold" color="gray.500" py={3} whiteSpace="pre-wrap" wordBreak="break-word">
          {note}
        </Td>
         <Td width="150px" fontSize="xs" fontWeight="semibold" color="gray.500" py={3}>
            {status ? (
                <Badge
                    px={2}
                    py={1}
                    borderRadius="sm"
                    fontSize="xs"
                    fontWeight="semibold"
                    colorScheme={
                        status.toLowerCase() === "completed" ? "green" :
                        status.toLowerCase() === "scheduled" ? "blue" :
                        status.toLowerCase() === "cancelled" ? "red" :
                        "gray"
                    }
                    >
                    {status}
                </Badge>
            ) : "N/A"}
        </Td>
      </Tr>
    </Tooltip>
  );
};

const MeetingHistory: React.FC<MeetingHistoryProps> = ({
  salesExecutiveName,
  meetings,
  isLoading,
  error,
  pagination,
  onPageChange,
}) => {
  const navigate = useNavigate();

  // The list is already scoped to this executive server-side, so render it as-is.
  const displayMeetings: ApiMeeting[] = meetings;

  const handleMeetingClick = (meeting: ApiMeeting) => {
    // Pass the full meeting object so the details page can resolve it even though
    // these scoped meetings aren't loaded into the shared global meeting context.
    navigate(`/meeting-details/${meeting.meeting_id}`, { state: { meeting } });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="200px" p={6} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="full" mt={4}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={6} mt={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="full">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle mr={2}>Error loading meeting history!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Box>
    );
  }
  
  if (!salesExecutiveName) {
      return (
        <Box p={6} mt={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="full">
            <Heading as="h2" fontSize="base" fontWeight="semibold" color="black" mb={4}>
              Meeting History:
            </Heading>
            <Text>Please select a sales executive to see their meeting history.</Text>
        </Box>
      );
  }
  
  if (displayMeetings.length === 0 && salesExecutiveName) {
     return (
        <Box p={6} mt={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="full">
            <Heading as="h2" fontSize="base" fontWeight="semibold" color="black" mb={4}>
              Meeting History:
            </Heading>
            <Text>No meeting history found for {salesExecutiveName}.</Text>
        </Box>
      );
  }

  return (
    <Box 
      overflow="hidden" 
      bg="white" 
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      w="full"
      mt={4}
    >
      <Box p={6}>
        <Heading as="h2" fontSize="base" fontWeight="semibold" color="black" mb={4}>
          Meeting History{salesExecutiveName ? `: ${salesExecutiveName}` : ''}
        </Heading>
        
        <Table variant="simple" size="sm">
          <Thead bg="blue.50">
            <Tr>
              <Th width="150px" fontWeight="medium" fontSize="xs" color="black">Client</Th>
              <Th width="130px" fontWeight="medium" fontSize="xs" color="black">Date</Th>
              <Th fontWeight="medium" fontSize="xs" color="black">Duration</Th>
              <Th width="150px" fontWeight="medium" fontSize="xs" color="black">Score</Th>
              <Th width="200px" fontWeight="medium" fontSize="xs" color="black">Note/Name</Th>
              <Th width="150px" fontWeight="medium" fontSize="xs" color="black">Status</Th> 
            </Tr>
          </Thead>
          <Tbody>
            {displayMeetings.map((meeting) => (
              <MeetingRow 
                key={meeting.id}
                client={meeting.client}
                date={meeting.date ? new Date(meeting.date).toLocaleDateString() : "N/A"}
                duration={meeting.duration || "N/A"}
                note={meeting.note || meeting.name || "N/A"}
                scorePercentage={meeting.scorePercentage === null ? undefined : meeting.scorePercentage}
                status={meeting.status}
                onClick={() => handleMeetingClick(meeting)}
                hasDetails={true}
              />
            ))}
          </Tbody>
        </Table>

        {pagination && pagination.totalPages > 1 && (
          <Flex justify="space-between" align="center" mt={4}>
            <Text fontSize="sm" color="gray.500">
              Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalItems} meetings
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPageChange(pagination.currentPage - 1)}
                isDisabled={pagination.currentPage <= 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPageChange(pagination.currentPage + 1)}
                isDisabled={pagination.currentPage >= pagination.totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default MeetingHistory; 